// The roles view — BR-37. Every role on the left, with how many people hold it; the chosen one on
// the right, its permissions as switches grouped Consulta / Operación / Administración.
//
// A holder of `access.roles.manage` on every client may change any role's permissions: the
// business's own roles, and the built-in ones too, for this business only (the backend keeps the
// change as an override and the catalogue's list as the default). Administrador is locked — it
// always holds everything, so a business can never take away the last way to manage roles and
// users. Nobody changes the permissions of a role they hold (that is editing their own access; the
// backend refuses it too). Edits stay a draft, counted in a bar at the foot of the panel, until
// "Guardar", which first says what the role gains and loses and who that reaches. Who holds a role
// is listed from its count, for whoever may manage roles or users (the backend's `holderUsers`).
//
// A switch offers only what its author may put in a role (`permisoIncluible`): a permission they
// hold on every client, or one the role already had. Deleting a role someone holds is a 409 from
// the backend, shown as it comes ("N usuario(s) tienen este rol").
import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Lock, Plus, RotateCcw, Trash2, Users, X } from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  Switch,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@mantis-sas/ui";

import { createAccessRole, deleteAccessRole, updateAccessRole, updateBuiltInRole } from "../../../ApiCalls/mantisAccess";
import {
  CODIGOS_DE_PERMISO,
  GRUPOS_DE_PERMISOS,
  PERMISOS,
  conNombresDePermiso,
  descripcionDePermiso,
  descripcionDeRol,
  nombreDePermiso,
  nombreDeRol,
} from "../../../utils/accesoDiccionario";
import {
  alcanceDe,
  cambiosDeRol,
  cambiosPendientesDeRol,
  permisoIncluible,
  puedeAdministrarRoles,
  rolEditable,
  ROLES,
  tieneElRol,
} from "../../../utils/cierreAcceso";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const AVISO_ERROR =
  "rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-2.5 py-1.5 text-[12.5px] text-brinks-danger";
const AVISO_INFO =
  "rounded-[8px] border border-brinks-border bg-brinks-surface px-2.5 py-1.5 text-[12.5px] text-brinks-muted-foreground";

const NUEVO = "nuevo";

const enOrden = (codigos) => CODIGOS_DE_PERMISO.filter((c) => codigos.includes(c));

const borradorDe = (rol) => ({
  name: rol ? rol.name : "",
  description: rol ? rol.description || "" : "",
  permissions: rol ? [...(rol.permissions || [])] : [],
});

const usuariosEnPalabras = (n) => (n === 1 ? "1 usuario" : `${n} usuarios`);
const cambiosEnPalabras = (n) => (n === 1 ? "1 cambio sin guardar" : `${n} cambios sin guardar`);

function Chips({ codigos, tono }) {
  const color = tono === "gana" ? "border-brinks-border bg-brinks-surface" : "border-brinks-danger-border bg-brinks-danger-soft";
  return (
    <span className="flex flex-wrap gap-1">
      {codigos.map((c) => (
        <span key={c} className={`inline-flex rounded-[6px] border px-1.5 py-0.5 text-[12px] text-brinks-foreground ${color}`}>
          {nombreDePermiso(c)}
        </span>
      ))}
    </span>
  );
}

const iniciales = (texto) =>
  String(texto || "?")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0].toUpperCase())
    .join("");

function ListaDeUsuarios({ usuarios }) {
  if (usuarios.length === 0) return <p className="text-[12.5px] text-brinks-muted-foreground">Nadie tiene este rol todavía.</p>;
  return (
    <ul className="max-h-[50vh] divide-y divide-brinks-border overflow-y-auto rounded-[8px] border border-brinks-border bg-card">
      {usuarios.map((u) => (
        <li key={u.id} className="flex items-center gap-2.5 px-3 py-1.5 text-[13px]">
          <span
            aria-hidden="true"
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brinks-surface text-[11px] font-semibold text-brinks-foreground"
          >
            {iniciales(u.name || u.email)}
          </span>
          <span className="min-w-0">
            <span className="block truncate font-medium text-brinks-foreground">{u.name || u.email}</span>
            {u.name && u.email ? <span className="block truncate text-[12px] text-brinks-muted-foreground">{u.email}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

const EN_RECUADRO = 8;

// "N usuarios": hovering it shows who they are in a small box (the first few, and how many more);
// clicking it, or focusing it and pressing Enter where there is no mouse, opens the whole list.
// Plain text when the backend did not say who they are.
function ConteoDeUsuarios({ rol, onVer }) {
  if (typeof rol?.holders !== "number") return null;
  const contenido = (
    <>
      <Users className="h-3.5 w-3.5" />
      {usuariosEnPalabras(rol.holders)}
    </>
  );
  if (!Array.isArray(rol.holderUsers) || rol.holders === 0) {
    return <span className="inline-flex items-center gap-1 text-[12.5px] text-brinks-muted-foreground">{contenido}</span>;
  }
  const visibles = rol.holderUsers.slice(0, EN_RECUADRO);
  const resto = rol.holderUsers.length - visibles.length;
  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onVer}
            className="inline-flex items-center gap-1 rounded-[6px] px-1.5 py-0.5 text-[12.5px] font-medium text-primary hover:bg-brinks-surface-hover hover:underline"
          >
            {contenido}
          </button>
        </TooltipTrigger>
        <TooltipContent
          align="end"
          className="brinks-scope font-redhat w-[260px] border border-brinks-border bg-card p-0 text-brinks-foreground shadow-md"
        >
          <p className="border-b border-brinks-border px-3 py-2 text-[11.5px] font-semibold uppercase tracking-wide text-brinks-muted-foreground">
            Tienen este rol
          </p>
          <ul className="space-y-1.5 px-3 py-2">
            {visibles.map((u) => (
              <li key={u.id} className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brinks-surface text-[10.5px] font-semibold"
                >
                  {iniciales(u.name || u.email)}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[12.5px] font-medium">{u.name || u.email}</span>
                  {u.name && u.email ? (
                    <span className="block truncate text-[11.5px] text-brinks-muted-foreground">{u.email}</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
          {resto > 0 ? (
            <p className="border-t border-brinks-border px-3 py-1.5 text-[11.5px] text-brinks-muted-foreground">
              y {resto} más · haz clic para ver todos
            </p>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function FilaDeRol({ rol, roles, activo, onElegir }) {
  return (
    <li>
      <button
        type="button"
        onClick={onElegir}
        aria-current={activo ? "true" : undefined}
        className={`flex w-full items-center justify-between gap-2 rounded-[8px] px-2.5 py-1.5 text-left text-[13px] transition ${
          activo ? "bg-primary text-primary-foreground" : "text-brinks-foreground hover:bg-brinks-surface-hover"
        }`}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate font-medium">{nombreDeRol(rol.id, roles)}</span>
          {rol.locked ? <Lock className="h-3.5 w-3.5 shrink-0" aria-label="No se puede cambiar" /> : null}
          {rol.modified ? (
            <span className={`shrink-0 text-[11px] ${activo ? "" : "text-brinks-muted-foreground"}`}>· Modificado</span>
          ) : null}
        </span>
        {typeof rol.holders === "number" ? (
          <span
            className={`inline-flex shrink-0 items-center gap-1 text-[11.5px] ${activo ? "" : "text-brinks-muted-foreground"}`}
            title={usuariosEnPalabras(rol.holders)}
          >
            <Users className="h-3 w-3" />
            {rol.holders}
          </span>
        ) : null}
      </button>
    </li>
  );
}

function ListaDeRoles({ roles, elegido, puedeCrear, onElegir }) {
  const predefinidos = roles.filter((r) => r.builtIn);
  const propios = roles.filter((r) => !r.builtIn);
  return (
    <nav {...guia("Roles", "Todos los roles: los de la plataforma y los propios de la empresa, con cuántas personas tiene cada uno. \"Modificado\" es un rol de la plataforma que esta empresa cambió. \"Nuevo rol\" crea uno propio.")} aria-label="Roles" className="space-y-3 rounded-[10px] border border-brinks-border bg-card p-2">
      <div>
        <p className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-brinks-muted-foreground">Predefinidos</p>
        <ul className="space-y-0.5">
          {predefinidos.map((rol) => (
            <FilaDeRol key={rol.id} rol={rol} roles={roles} activo={elegido === rol.id} onElegir={() => onElegir(rol.id)} />
          ))}
        </ul>
      </div>
      <div>
        <p className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-brinks-muted-foreground">
          Propios del negocio
        </p>
        {propios.length === 0 ? (
          <p className="px-2.5 py-1 text-[12px] text-brinks-muted-foreground">Todavía no hay roles propios.</p>
        ) : (
          <ul className="space-y-0.5">
            {propios.map((rol) => (
              <FilaDeRol key={rol.id} rol={rol} roles={roles} activo={elegido === rol.id} onElegir={() => onElegir(rol.id)} />
            ))}
          </ul>
        )}
        {puedeCrear ? (
          <Button
            type="button"
            size="sm"
            variant={elegido === NUEVO ? "default" : "outline"}
            className="mt-2 h-8 w-full gap-1.5"
            onClick={() => onElegir(NUEVO)}
          >
            <Plus className="h-4 w-4" />
            Nuevo rol
          </Button>
        ) : null}
      </div>
    </nav>
  );
}

function GrupoDePermisos({ grupo, me, rol, borrador, editable, onCambiar }) {
  const codigos = CODIGOS_DE_PERMISO.filter((c) => PERMISOS[c].grupo === grupo.clave);
  const actuales = rol ? rol.permissions || [] : [];
  const incluibles = codigos.filter((c) => permisoIncluible(me, c, actuales));
  const activos = codigos.filter((c) => borrador.permissions.includes(c));
  const todosActivos = incluibles.length > 0 && incluibles.every((c) => borrador.permissions.includes(c));

  const alternarGrupo = () => {
    const resto = borrador.permissions.filter((c) => !incluibles.includes(c));
    onCambiar(todosActivos ? resto : [...resto, ...incluibles]);
  };

  return (
    <fieldset className="rounded-[10px] border border-brinks-border">
      <legend className="sr-only">{grupo.titulo}</legend>
      <div className="flex items-center justify-between gap-2 border-b border-brinks-border px-3 py-2">
        <p className="text-[11.5px] font-semibold uppercase tracking-wide text-brinks-muted-foreground">
          {grupo.titulo} <span className="font-normal normal-case">· {activos.length} de {codigos.length}</span>
        </p>
        {editable && incluibles.length > 0 ? (
          <button type="button" className="text-[12px] font-medium text-primary hover:underline" onClick={alternarGrupo}>
            {todosActivos ? "Quitar todos" : "Activar todos"}
          </button>
        ) : null}
      </div>
      <ul className="divide-y divide-brinks-border">
        {codigos.map((codigo) => {
          const id = `permiso-${codigo}`;
          const incluible = incluibles.includes(codigo);
          const activo = borrador.permissions.includes(codigo);
          return (
            <li key={codigo} className="flex items-start justify-between gap-3 px-3 py-2">
              <label htmlFor={id} className={`min-w-0 text-[13px] ${editable && incluible ? "cursor-pointer" : ""}`}>
                <span className="font-medium text-brinks-foreground">{nombreDePermiso(codigo)}</span>
                <span className="block text-[12px] text-brinks-muted-foreground">
                  {editable && !incluible
                    ? "No lo tienes sobre todos los clientes, así que no puedes incluirlo."
                    : descripcionDePermiso(codigo)}
                </span>
              </label>
              <Switch
                id={id}
                className="mt-0.5 shrink-0"
                checked={activo}
                disabled={!editable || !incluible}
                onCheckedChange={() =>
                  onCambiar(activo ? borrador.permissions.filter((c) => c !== codigo) : [...borrador.permissions, codigo])
                }
              />
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}

function PanelDeRol({
  me,
  rol,
  roles,
  borrador,
  setBorrador,
  editable,
  editableDatos,
  propio,
  pendientes,
  enviando,
  error,
  onGuardar,
  onDescartar,
  onEliminar,
  onVerUsuarios,
}) {
  const nuevo = !rol;
  const alejado = rol && rol.builtIn && rol.defaultPermissions ? cambiosDeRol(rol.defaultPermissions, rol.permissions) : null;
  // Only on a role this business changed: on one still on its defaults, putting them back is "Descartar".
  const restaurable =
    editable &&
    rol?.builtIn &&
    rol.modified &&
    rol.defaultPermissions &&
    cambiosPendientesDeRol({ ...rol, permissions: rol.defaultPermissions }, borrador) > 0;
  const nombreValido = rol?.builtIn || borrador.name.trim().length > 0;

  return (
    <section {...guia("Permisos del rol", "Lo que puede ver y hacer quien tiene este rol, en interruptores agrupados en Consulta, Operación y Administración. Los cambios se acumulan abajo hasta que los guardas o los descartas. Administrador no se puede cambiar, y nadie cambia un rol que tiene.")} aria-label={nuevo ? "Nuevo rol" : nombreDeRol(rol.id, roles)} className="flex min-h-[420px] flex-col rounded-[10px] border border-brinks-border bg-card">
      <header className="space-y-1.5 border-b border-brinks-border p-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <h2 className="text-[16px] font-semibold text-brinks-foreground">{nuevo ? "Nuevo rol" : nombreDeRol(rol.id, roles)}</h2>
          {rol ? <Badge variant={rol.builtIn ? "secondary" : "outline"}>{rol.builtIn ? "Predefinido" : "Propio"}</Badge> : null}
          {rol?.modified ? <Badge variant="outline">Modificado</Badge> : null}
          {rol?.clientBound ? <Badge variant="outline">Un solo cliente</Badge> : null}
          <span className="ml-auto">
            <ConteoDeUsuarios rol={rol} onVer={onVerUsuarios} />
          </span>
        </div>
        {rol && descripcionDeRol(rol) ? <p className="text-[12.5px] text-brinks-muted-foreground">{descripcionDeRol(rol)}</p> : null}
        {alejado && (alejado.gana.length > 0 || alejado.pierde.length > 0) ? (
          <div className="space-y-1 text-[12.5px] text-brinks-muted-foreground">
            <p>Este negocio cambió sus permisos predeterminados:</p>
            {alejado.gana.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1">
                <span>Agregados:</span>
                <Chips codigos={alejado.gana} tono="gana" />
              </div>
            ) : null}
            {alejado.pierde.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1">
                <span>Quitados:</span>
                <Chips codigos={alejado.pierde} tono="pierde" />
              </div>
            ) : null}
          </div>
        ) : null}
      </header>

      <div className="flex-1 space-y-3 p-4">
        {rol?.locked ? (
          <p className={AVISO_INFO}>
            <Lock className="mr-1 inline h-3.5 w-3.5" />
            El Administrador siempre tiene todos los permisos, para que el negocio nunca se quede sin quien administre
            usuarios y roles. No se puede cambiar.
          </p>
        ) : null}

        {propio && !rol.locked && puedeAdministrarRoles(me) ? (
          <p className={AVISO_INFO}>
            <Lock className="mr-1 inline h-3.5 w-3.5" />
            Tienes este rol asignado, así que no puedes cambiar sus permisos: sería cambiar tu propio acceso. Pídeselo a
            otro administrador.
          </p>
        ) : null}

        {editableDatos ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rol-nombre">Nombre</Label>
              <Input
                id="rol-nombre"
                className="bg-card text-brinks-foreground"
                maxLength={60}
                value={borrador.name}
                onChange={(e) => setBorrador({ ...borrador, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rol-descripcion">Descripción</Label>
              <Textarea
                id="rol-descripcion"
                className="min-h-9 bg-card text-brinks-foreground"
                maxLength={300}
                rows={1}
                value={borrador.description}
                onChange={(e) => setBorrador({ ...borrador, description: e.target.value })}
              />
            </div>
          </div>
        ) : null}

        {GRUPOS_DE_PERMISOS.map((grupo) => (
          <GrupoDePermisos
            key={grupo.clave}
            grupo={grupo}
            me={me}
            rol={rol}
            borrador={borrador}
            editable={editable}
            onCambiar={(permisos) => setBorrador({ ...borrador, permissions: enOrden(permisos) })}
          />
        ))}

        {error ? (
          <p role="alert" className={AVISO_ERROR}>
            {error}
          </p>
        ) : null}

        {editableDatos && rol ? (
          <div className="pt-1">
            <Button type="button" size="sm" variant="ghost" className="h-8 gap-1 text-brinks-danger" onClick={onEliminar}>
              <Trash2 className="h-3.5 w-3.5" />
              Eliminar rol
            </Button>
          </div>
        ) : null}
      </div>

      {(editable || editableDatos) && (pendientes > 0 || restaurable || nuevo) ? (
        <footer className="sticky bottom-0 flex flex-wrap items-center gap-2 rounded-b-[10px] border-t border-brinks-border bg-card px-4 py-2.5">
          <span className="text-[12.5px] font-medium text-brinks-foreground" aria-live="polite">
            {pendientes > 0 ? cambiosEnPalabras(pendientes) : nuevo ? "Elige un nombre y sus permisos" : ""}
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {restaurable ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 gap-1"
                onClick={() => setBorrador({ ...borrador, permissions: [...rol.defaultPermissions] })}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Restaurar predeterminados
              </Button>
            ) : null}
            {pendientes > 0 ? (
              <Button type="button" size="sm" variant="outline" className="h-8" disabled={enviando} onClick={onDescartar}>
                Descartar
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              className="h-8"
              disabled={enviando || pendientes === 0 || !nombreValido || borrador.permissions.length === 0}
              onClick={onGuardar}
            >
              {enviando ? <Spinner className="mr-1.5 h-4 w-4" /> : null}
              {nuevo ? "Crear rol" : "Guardar"}
            </Button>
          </div>
        </footer>
      ) : null}
    </section>
  );
}

function Confirmacion({ abierto, titulo, onCerrar, children, accion, tonoAccion, enviando, onConfirmar }) {
  return (
    <Dialog open={abierto} onOpenChange={(open) => !open && onCerrar()}>
      <DialogContent className="brinks-scope font-redhat max-w-md gap-0 p-0">
        <DialogHeader className="border-b p-4 text-left">
          <div className="flex min-w-0 items-center gap-1">
            <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">{titulo}</DialogTitle>
            <BotonGuia guiaId="dialogo-confirmar-rol" titulo="Confirmar" texto="Confirma el cambio en el rol. Lo que dice abajo es lo que va a pasar." nombre="Confirmar" />
          </div>
        </DialogHeader>
        <div className="space-y-2.5 p-4 text-[13px] text-brinks-foreground">{children}</div>
        <DialogFooter className="gap-2 border-t p-3">
          <Button type="button" variant="outline" onClick={onCerrar} disabled={enviando}>
            Cancelar
          </Button>
          <Button type="button" variant={tonoAccion} onClick={onConfirmar} disabled={enviando}>
            {enviando ? <Spinner className="mr-1.5 h-4 w-4" /> : null}
            {accion}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// One block of the save summary: what the role gains (green, +) or loses (red, −), each permission
// with its description so the reader knows what it means without going back to the panel.
function BloqueDeCambio({ codigos, tono }) {
  if (codigos.length === 0) return null;
  const gana = tono === "gana";
  const Icono = gana ? Check : X;
  return (
    <section
      aria-label={gana ? "Se agregan" : "Se quitan"}
      className={`rounded-[10px] border ${
        gana ? "border-brinks-success-border bg-brinks-success-soft" : "border-brinks-danger-border bg-brinks-danger-soft"
      }`}
    >
      <p
        className={`px-3 pt-2.5 text-[11.5px] font-semibold uppercase tracking-wide ${gana ? "text-brinks-success" : "text-brinks-danger"}`}
      >
        {gana ? "+ Se agregan" : "− Se quitan"} ({codigos.length})
      </p>
      <ul className="space-y-1.5 px-3 pb-2.5 pt-1.5">
        {codigos.map((c) => (
          <li key={c} className="flex items-start gap-2">
            <Icono className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${gana ? "text-brinks-success" : "text-brinks-danger"}`} />
            <span className="min-w-0">
              <span className="block text-[13px] font-medium text-brinks-foreground">{nombreDePermiso(c)}</span>
              <span className="block text-[12px] text-brinks-muted-foreground">{descripcionDePermiso(c)}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// Before a role is saved: what changes, and who it reaches — the list one click away.
function ResumenDeCambios({ abierto, nombre, rol, gana, pierde, verUsuarios, onVerUsuarios, enviando, onCerrar, onConfirmar }) {
  const sinCambiosDePermisos = gana.length === 0 && pierde.length === 0;
  const conLista = typeof rol?.holders === "number" && rol.holders > 0 && Array.isArray(rol.holderUsers);
  return (
    <Dialog open={abierto} onOpenChange={(open) => !open && onCerrar()}>
      <DialogContent className="brinks-scope font-redhat max-h-[90vh] max-w-md gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-1 border-b p-4 text-left">
          <div className="flex min-w-0 items-center gap-1">
            <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">Guardar cambios en {nombre}</DialogTitle>
            <BotonGuia guiaId="dialogo-guardar-rol" titulo="Guardar cambios del rol" texto="Lo que el rol gana y pierde, y a cuántas personas les llega. Revísalo antes de aplicarlo." nombre="Guardar cambios del rol" />
          </div>
          <p className="text-[12.5px] text-brinks-muted-foreground">Revisa qué cambia antes de aplicarlo.</p>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-2.5 overflow-y-auto p-4">
          <BloqueDeCambio codigos={gana} tono="gana" />
          <BloqueDeCambio codigos={pierde} tono="pierde" />
          {sinCambiosDePermisos ? (
            <p className={AVISO_INFO}>Cambia el nombre o la descripción; los permisos quedan igual.</p>
          ) : null}

          {typeof rol?.holders === "number" ? (
            <section {...guia("A quién afecta", "Cuántas personas reciben el cambio.")} aria-label="A quién afecta" className="rounded-[10px] border border-brinks-border bg-brinks-surface">
              <div className="flex items-start gap-2.5 p-3">
                <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-card">
                  <Users className="h-3.5 w-3.5 text-brinks-foreground" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-brinks-foreground">
                    {rol.holders === 0 ? "Nadie tiene este rol todavía" : `${usuariosEnPalabras(rol.holders)} tienen este rol`}
                  </p>
                  <p className="text-[12px] text-brinks-muted-foreground">
                    {rol.holders === 0 ? "El cambio aplicará a quien lo reciba." : "El cambio aplica desde su próxima acción en Mantis."}
                  </p>
                </div>
                {conLista ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 shrink-0 gap-1 bg-card"
                    aria-expanded={verUsuarios}
                    onClick={onVerUsuarios}
                  >
                    {verUsuarios ? "Ocultar" : "Ver usuarios"}
                    <ChevronDown className={`h-3.5 w-3.5 transition ${verUsuarios ? "rotate-180" : ""}`} />
                  </Button>
                ) : null}
              </div>
              {conLista && verUsuarios ? (
                <div className="border-t border-brinks-border p-2">
                  <ListaDeUsuarios usuarios={rol.holderUsers} />
                </div>
              ) : null}
            </section>
          ) : null}
        </div>

        <DialogFooter className="gap-2 border-t p-3">
          <Button type="button" variant="outline" onClick={onCerrar} disabled={enviando}>
            Cancelar
          </Button>
          <Button type="button" onClick={onConfirmar} disabled={enviando}>
            {enviando ? <Spinner className="mr-1.5 h-4 w-4" /> : null}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AccesoRoles({ token, me, roles, onCambio }) {
  const [elegido, setElegido] = useState(null); // role id | NUEVO
  const [borrador, setBorrador] = useState(borradorDe(null));
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [confirmando, setConfirmando] = useState(null); // null | "guardar" | "eliminar" | { ir: id }
  const [viendoUsuarios, setViendoUsuarios] = useState(false); // the list dialog opened from the count
  const [usuariosEnResumen, setUsuariosEnResumen] = useState(false); // the list inside the save summary

  const puede = puedeAdministrarRoles(me);
  // Held on some clients but not all: the permission exists, and still does not let them write one.
  const parcial = !puede && (alcanceDe(me, ROLES).clients || []).length > 0;

  const rol = useMemo(() => (elegido && elegido !== NUEVO ? roles.find((r) => r.id === elegido) || null : null), [roles, elegido]);
  const editable = elegido === NUEVO ? puede : rolEditable(me, rol);
  // A custom role's name and description: renaming a role you hold changes nobody's access.
  const editableDatos = elegido === NUEVO ? puede : puede && Boolean(rol) && !rol.builtIn;
  const propio = tieneElRol(me, rol);
  const pendientes = elegido === NUEVO ? cambiosPendientesDeRol(null, borrador) : rol ? cambiosPendientesDeRol(rol, borrador) : 0;

  // The first role once the list arrives; the same role again after a reload, as saved.
  useEffect(() => {
    if (roles.length === 0) return;
    if (elegido === NUEVO) return;
    const actual = roles.find((r) => r.id === elegido) || roles[0];
    setElegido(actual.id);
    setBorrador(borradorDe(actual));
    // `elegido` is read, not followed: choosing a role sets the draft itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles]);

  const ir = (id) => {
    setElegido(id);
    setBorrador(borradorDe(id === NUEVO ? null : roles.find((r) => r.id === id)));
    setError("");
  };

  const elegir = (id) => {
    if (id === elegido) return;
    if (pendientes > 0) setConfirmando({ ir: id });
    else ir(id);
  };

  const guardar = async () => {
    setEnviando(true);
    setError("");
    const permissions = enOrden(borrador.permissions);
    let peticion;
    if (elegido === NUEVO) peticion = createAccessRole(token, { ...borrador, permissions });
    else if (rol.builtIn) peticion = updateBuiltInRole(token, rol.id, permissions);
    else peticion = updateAccessRole(token, rol.id, { ...borrador, permissions });
    const response = await peticion.catch(() => null);
    setEnviando(false);
    setConfirmando(null);
    if (!response?.success) {
      setError(conNombresDePermiso(response?.message) || "No se pudo guardar el rol.");
      return;
    }
    if (elegido === NUEVO && response.data?.role?.id) setElegido(response.data.role.id);
    onCambio();
  };

  const eliminar = async () => {
    setEnviando(true);
    setError("");
    const response = await deleteAccessRole(token, rol.id).catch(() => null);
    setEnviando(false);
    setConfirmando(null);
    if (!response?.success) {
      setError(conNombresDePermiso(response?.message) || "No se pudo eliminar el rol.");
      return;
    }
    setElegido(null);
    onCambio();
  };

  const nombre = rol ? nombreDeRol(rol.id, roles) : borrador.name.trim() || "el rol nuevo";
  const { gana, pierde } = cambiosDeRol(rol ? rol.permissions : [], borrador.permissions);

  return (
    <div className="space-y-3">
      <p className="max-w-3xl text-[12.5px] text-brinks-muted-foreground">
        Un rol dice qué puede hacer una persona; dónde, se decide al asignárselo.
        {puede ? " Elige un rol para ver o cambiar sus permisos. Los cambios aplican a todas las personas que lo tienen." : ""}
      </p>
      {parcial ? (
        <p className="text-[12.5px] text-brinks-muted-foreground">
          Cambiar o crear roles requiere el permiso «Administrar roles» sobre todos los clientes.
        </p>
      ) : null}

      <div className="grid items-start gap-3 lg:grid-cols-[260px_minmax(0,1fr)]">
        <ListaDeRoles roles={roles} elegido={elegido} puedeCrear={puede} onElegir={elegir} />
        {elegido === NUEVO || rol ? (
          <PanelDeRol
            key={elegido}
            me={me}
            rol={rol}
            roles={roles}
            borrador={borrador}
            setBorrador={setBorrador}
            editable={editable}
            editableDatos={editableDatos}
            propio={propio}
            pendientes={pendientes}
            enviando={enviando}
            error={error}
            onGuardar={() => {
              if (elegido === NUEVO) {
                guardar();
                return;
              }
              setUsuariosEnResumen(false);
              setConfirmando("guardar");
            }}
            onDescartar={() => ir(elegido)}
            onEliminar={() => setConfirmando("eliminar")}
            onVerUsuarios={() => setViendoUsuarios(true)}
          />
        ) : (
          <div className="flex min-h-[200px] items-center justify-center rounded-[10px] border border-brinks-border bg-card">
            <Spinner className="h-5 w-5" />
          </div>
        )}
      </div>

      <ResumenDeCambios
        abierto={confirmando === "guardar"}
        nombre={nombre}
        rol={rol}
        gana={gana}
        pierde={pierde}
        verUsuarios={usuariosEnResumen}
        onVerUsuarios={() => setUsuariosEnResumen((v) => !v)}
        enviando={enviando}
        onCerrar={() => setConfirmando(null)}
        onConfirmar={guardar}
      />

      <Dialog open={viendoUsuarios && Boolean(rol)} onOpenChange={(open) => !open && setViendoUsuarios(false)}>
        <DialogContent className="brinks-scope font-redhat max-w-md gap-0 p-0">
          <DialogHeader className="border-b p-4 text-left">
            <div className="flex min-w-0 items-center gap-1">
              <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">
                {rol ? `Usuarios con el rol ${nombreDeRol(rol.id, roles)}` : ""}
              </DialogTitle>
              <BotonGuia guiaId="dialogo-usuarios-del-rol" titulo="Usuarios con el rol" texto="Las personas que tienen este rol." nombre="Usuarios con el rol" />
            </div>
          </DialogHeader>
          <div className="p-4">
            <ListaDeUsuarios usuarios={(rol && rol.holderUsers) || []} />
          </div>
          <DialogFooter className="border-t p-3">
            <Button type="button" variant="outline" onClick={() => setViendoUsuarios(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Confirmacion
        abierto={confirmando === "eliminar"}
        titulo={`Eliminar ${nombre}`}
        accion="Eliminar"
        tonoAccion="destructive"
        enviando={enviando}
        onCerrar={() => setConfirmando(null)}
        onConfirmar={eliminar}
      >
        <p>El rol se elimina para todo el negocio. Solo se puede eliminar si nadie lo tiene asignado.</p>
      </Confirmacion>

      <Confirmacion
        abierto={Boolean(confirmando && confirmando.ir)}
        titulo="Tienes cambios sin guardar"
        accion="Descartar cambios"
        tonoAccion="destructive"
        onCerrar={() => setConfirmando(null)}
        onConfirmar={() => {
          const destino = confirmando.ir;
          setConfirmando(null);
          ir(destino);
        }}
      >
        <p>
          {pendientes === 1 ? `El cambio en ${nombre} se perderá` : `Los ${pendientes} cambios en ${nombre} se perderán`} si
          cambias de rol.
        </p>
      </Confirmacion>
    </div>
  );
}
